import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, Skeleton, EmptyState } from '../../../ui';
import { ChartPanel } from '../../../components/atlas/ChartPanel';
import { useChartColors } from '../../../utils/chartTokens';
import { leadsService } from '../../../services/leads.service';
import { useProduct } from '../../../contexts/ProductContext';

interface KpiProps {
  label: string;
  value: string;
  sub?: string;
}

function Kpi({ label, value, sub }: KpiProps) {
  return (
    <Card padding="md">
      <p className="text-xs uppercase tracking-wider text-ink-tertiary">{label}</p>
      <p className="font-mono text-2xl font-semibold text-ink-primary mt-1">{value}</p>
      {sub && <p className="text-xs text-ink-secondary mt-1">{sub}</p>}
    </Card>
  );
}

export function SaudeTab() {
  const { selectedProductId } = useProduct();
  const colors = useChartColors();

  const { data: leadsResp, isLoading } = useQuery({
    queryKey: ['insights-saude', selectedProductId],
    queryFn: () => leadsService.list({ productId: selectedProductId, limit: 1000 }),
    enabled: !!selectedProductId,
  });

  if (!selectedProductId) {
    return <EmptyState title="Selecione um produto" body="Saúde do pipeline é por produto." />;
  }
  if (isLoading) {
    return <Skeleton height={300} />;
  }

  const leads: any[] =
    (leadsResp as any)?.leads ??
    (leadsResp as any)?.items ??
    (Array.isArray(leadsResp) ? leadsResp : []);
  const total = leads.length;
  const now = new Date();
  const stale = leads.filter((l) => {
    if (!l.lastContactAt) {
      return true;
    }
    const ms = now.getTime() - new Date(l.lastContactAt).getTime();
    return ms > 30 * 24 * 60 * 60 * 1000;
  }).length;
  const won = leads.filter((l) => l.stage?.type === 'WON' || l.stageType === 'WON').length;
  const lost = leads.filter((l) => l.stage?.type === 'LOST' || l.stageType === 'LOST').length;
  const closed = won + lost;
  const winRate = closed > 0 ? Math.round((won / closed) * 100) : 0;
  const avgDealValue =
    leads.reduce((acc, l) => acc + Number(l.dealValue ?? 0), 0) / Math.max(total, 1);

  // Distribution by stage
  const byStage = leads.reduce<Record<string, number>>((acc, l) => {
    const name = l.stage?.name ?? l.stageName ?? 'Sem stage';
    acc[name] = (acc[name] ?? 0) + 1;
    return acc;
  }, {});
  const stageData = Object.entries(byStage).map(([stage, count]) => ({ stage, count }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total" value={String(total)} />
        <Kpi label="Win rate" value={`${winRate}%`} sub={`${won} ganhos / ${lost} perdas`} />
        <Kpi
          label="Ticket médio"
          value={`R$ ${Math.round(avgDealValue).toLocaleString('pt-BR')}`}
        />
        <Kpi label="Stale (>30d)" value={String(stale)} sub="Sem contato recente" />
      </div>
      <ChartPanel title="Distribuição por stage" subtitle={`${total} leads no pipeline`}>
        {stageData.length === 0 ? (
          <EmptyState title="Sem leads" body="Nenhum lead cadastrado." />
        ) : (
          <ResponsiveContainer>
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="stage" stroke={colors.inkTer} fontSize={11} />
              <YAxis allowDecimals={false} stroke={colors.inkTer} fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: colors.ink }}
                itemStyle={{ color: colors.inkSec }}
              />
              <Bar dataKey="count" fill={colors.info} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>
    </div>
  );
}
